// Manufacturing → Admin Home mode panel.
// Hosts admin-only actions (scaffolded create flows) and the org Activity
// feed sourced from `public.audit_log`. The Activity card renders entries
// for any `mfg_*` entity type; today only `mfg_material` writes are wired,
// but the list will start showing recipe / cost-entry events as those
// domains opt in to `postAuditLog()`.
import { useEffect, useMemo, useState } from "react";
import { SectionCard } from "@/components/common/SectionCard";
import { ActivityTimelineList } from "@/components/activity/ActivityTimelineList";
import { auditLogEntryToTimelineEntry } from "@/components/activity/activityTimelineAdapters";
import { ManufacturingGateBanner, ManufacturingToast } from "./ManufacturingGateBanner";
import { ManufacturingPanelModal } from "./ManufacturingPanelModal";
import { useManufacturingAuditLog } from "./useManufacturingAuditLog";
import { useManufacturingCreateMaterial } from "./useManufacturingCreateMaterial";
import { useManufacturingEntitlementGate } from "./useManufacturingEntitlementGate";
import { useManufacturingMaterials } from "./useManufacturingMaterials";
import type { MfgModalKind } from "./mfgPanelTypes";
import { ghostBtnClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import { ManufacturingOrgSerialConventionCard } from "./ManufacturingOrgSerialConventionCard";
import { ManufacturingSignTemplateSubscriptionsStub } from "./ManufacturingSignTemplateSubscriptionsStub";

const ACTIVITY_FILTERS = { entity_type: "mfg_material", limit: 25 } as const;

export function ManufacturingAdminHomePanel() {
  const gate = useManufacturingEntitlementGate();
  const materials = useManufacturingMaterials({ enabled: gate.enabled });
  const createMaterial = useManufacturingCreateMaterial({ onCreated: materials.refresh });
  const audit = useManufacturingAuditLog({
    enabled: gate.enabled,
    filters: ACTIVITY_FILTERS,
  });
  const timelineEntries = useMemo(
    () => audit.entries.map(auditLogEntryToTimelineEntry),
    [audit.entries]
  );
  const [drillEntityId, setDrillEntityId] = useState<string | null>(null);
  const drilledEntries = useMemo(() => {
    if (!drillEntityId) return timelineEntries;
    return timelineEntries.filter(
      (e) =>
        e.entity_id === drillEntityId ||
        e.metadata.entity_id === drillEntityId ||
        e.metadata.lead_id === drillEntityId
    );
  }, [timelineEntries, drillEntityId]);
  const [openModal, setOpenModal] = useState<MfgModalKind | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  function scaffoldAction(name: string) {
    setToast(`${name} is scaffolded (UI only)`);
  }

  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(null), 2400);
    return () => window.clearTimeout(t);
  }, [toast]);

  useEffect(() => {
    if (!createMaterial.toast) return;
    const t = window.setTimeout(createMaterial.dismissToast, 2400);
    return () => window.clearTimeout(t);
  }, [createMaterial.toast, createMaterial.dismissToast]);

  return (
    <div className="w-full text-left space-y-4">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <h2 className="text-lg font-bold text-gray-900 dark:text-zinc-100">Admin Home</h2>
      </div>

      <ManufacturingToast message={toast ?? createMaterial.toast} />
      <ManufacturingGateBanner gate={gate} />

      {gate.enabled ? (
        <>
          <SectionCard title="Admin actions">
            <div className="flex flex-wrap gap-2">
              <button type="button" className={ghostBtnClass} onClick={() => setOpenModal("building")}>
                + New building
              </button>
              <button type="button" className={ghostBtnClass} onClick={() => setOpenModal("material")}>
                + New material
              </button>
              <button type="button" className={ghostBtnClass} onClick={() => setOpenModal("vendor")}>
                + Add vendor
              </button>
              <button type="button" className={ghostBtnClass} onClick={() => setOpenModal("user")}>
                + Add user
              </button>
            </div>
            <div className="mt-3 rounded-xl border border-gray-100 bg-gray-50 p-3 dark:border-zinc-700 dark:bg-zinc-800/50">
              <div className="text-sm font-medium text-gray-900 dark:text-zinc-100">BOM editor</div>
              <div className="text-xs text-gray-600 dark:text-zinc-400">Scaffolded UI</div>
            </div>
          </SectionCard>

          <ManufacturingOrgSerialConventionCard />

          <ManufacturingSignTemplateSubscriptionsStub />

          <SectionCard
            title="Activity"
            rightSlot={
              <button
                type="button"
                className={ghostBtnClass}
                onClick={() => void audit.refresh()}
                disabled={audit.working}
              >
                {audit.working ? "Refreshing…" : "Refresh"}
              </button>
            }
          >
            <ActivityTimelineList
              entries={drilledEntries}
              total={drillEntityId ? drilledEntries.length : audit.total}
              loading={audit.loading}
              error={audit.error}
              scope="org"
              onDrillEntity={(entityId) => {
                setDrillEntityId(entityId);
              }}
              drillEntityId={drillEntityId}
              onClearDrill={() => {
                setDrillEntityId(null);
              }}
              emptyMessage="No activity yet."
            />
          </SectionCard>

          <SectionCard title="Profile">
            <ul className="divide-y divide-gray-100 dark:divide-zinc-700">
              <li className="py-2.5">
                <div className="text-sm font-medium text-gray-900 dark:text-zinc-100">Plant user</div>
                <div className="text-xs text-gray-600 dark:text-zinc-400">Manufacturing access</div>
              </li>
              <li>
                <button
                  type="button"
                  className="w-full rounded-md px-3 py-2 text-left text-sm font-medium text-red-700 hover:bg-red-50 dark:text-red-300 dark:hover:bg-red-950/40"
                  onClick={() => scaffoldAction("Sign Out")}
                >
                  Sign out
                </button>
              </li>
            </ul>
          </SectionCard>
        </>
      ) : null}

      {openModal ? (
        <ManufacturingPanelModal
          openModal={openModal}
          setOpenModal={setOpenModal}
          onCreateMaterial={createMaterial.submit}
        />
      ) : null}
    </div>
  );
}
